import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Search, ShieldCheck, BookOpen, Leaf, Users, ArrowRight, MoveRight, MessageCircleQuestion } from "lucide-react";
import { PageHero } from "@/components/PageHero";
import { SectionHead } from "@/components/SectionHead";
import { Reveal, Stagger, StaggerItem, ParallaxImage } from "@/lib/motion";
import { ACADEMY_CARDS, STATS } from "@/data/content";
import { IMG } from "@/data/images";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PILLARS = [
  { icon: ShieldCheck, title: "Trustworthy Information", text: "Well-researched and expert-verified content you can trust." },
  { icon: BookOpen, title: "Easy to Understand", text: "Simple language & practical insights for everyone." },
  { icon: Leaf, title: "Rooted in Nature", text: "Inspired by nature, inspired by traditional wisdom." },
  { icon: Users, title: "For Every Family", text: "Knowledge that helps you make better choices every day." },
];

const PATHS = [
  { title: "Become a Moringa Expert", sub: "Master everything about the miracle tree.", progress: 75, image: IMG.heroCollection },
  { title: "Ashwagandha: Ancient Wisdom", sub: "Discover the adaptogenic power of ashwagandha.", progress: 50, image: IMG.ashwagandha },
  { title: "The Power of Amla", sub: "The natural source of Vitamin C and more.", progress: 30, image: IMG.amla },
  { title: "Spirulina Explained", sub: "The super algae for energy and immunity.", progress: 65, image: IMG.spirulina },
  { title: "Millets for Everyday Wellness", sub: "Rediscover ancient grains for modern health.", progress: 40, image: IMG.farmerPortrait },
];

export default function Academy() {
  const [query, setQuery] = useState("");
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [busy, setBusy] = useState(false);

  const filtered = ACADEMY_CARDS.filter((c) =>
    !query || `${c.title} ${c.desc} ${c.tags.join(" ")}`.toLowerCase().includes(query.toLowerCase())
  );

  const ask = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email.includes("@") || !form.message) return toast.error("Please complete all fields");
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/messages`, { ...form, topic: "ask-nutribest" });
      toast.success(data.message);
      setForm({ name: "", email: "", message: "" });
    } catch {
      toast.error("Could not send your question. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="academy-page">
      <PageHero
        eyebrow="The Nutribest Academy"
        lines={["Learn. Nourish.", "Thrive."]}
        sub="Knowledge is the foundation of healthier living. Explore trusted insights on superfoods, nutrition, recipes, farming, research and wellness — crafted by experts, rooted in nature, backed by science."
        image={IMG.heroAcademy}
      >
        <div className="relative max-w-lg">
          <input
            data-testid="academy-search-input"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="What would you like to learn today?"
            className="w-full bg-cream text-ink rounded-full pl-6 pr-14 py-4 text-sm shadow-2xl focus:outline-none focus:ring-2 focus:ring-gold placeholder:text-mutedgreen/70"
          />
          <span className="absolute right-2 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-forest text-cream flex items-center justify-center"><Search size={16} /></span>
        </div>
      </PageHero>

      <section className="py-20 sm:py-24">
        <div className="container-nb">
          <SectionHead
            eyebrow="Welcome to the Nutribest Academy"
            title="More than information. A journey towards better health."
            sub="At Nutribest, we believe that the best choices begin with understanding. The Nutribest Academy is your trusted resource for learning about nutrition, superfoods, healthy living, and the science behind nature's finest ingredients."
          />
          <Stagger className="grid grid-cols-2 lg:grid-cols-4 gap-6">
            {PILLARS.map(({ icon: Icon, title, text }) => (
              <StaggerItem key={title}>
                <div className="text-center p-6 bg-white border border-forest/10 rounded-2xl h-full hover:border-gold/40 transition-colors duration-500">
                  <span className="mx-auto w-12 h-12 rounded-full border border-gold/40 flex items-center justify-center text-forest"><Icon size={20} strokeWidth={1.5} /></span>
                  <h3 className="mt-4 text-[11px] font-bold uppercase tracking-[0.14em] text-forest">{title}</h3>
                  <p className="mt-2 text-[11px] text-mutedgreen leading-relaxed">{text}</p>
                </div>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </section>

      <section className="py-20 sm:py-24 bg-creamdark/40 border-y border-forest/10">
        <div className="container-nb">
          <SectionHead eyebrow="Explore our learning library" title={query ? `Results for "${query}"` : "Eight worlds of knowledge."} />
          {filtered.length === 0 && <p data-testid="academy-no-results" className="text-center text-mutedgreen text-sm">Nothing found — try "moringa", "recipes" or "farming".</p>}
          <Stagger className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {filtered.map(({ icon: Icon, title, desc, tags, cta, badge }) => (
              <StaggerItem key={title}>
                <div data-testid={`academy-card-${title.toLowerCase().replace(/[^a-z]+/g, "-")}`} className="group h-full bg-white rounded-2xl border border-forest/10 p-6 flex flex-col hover:shadow-xl hover:shadow-forest/10 hover:-translate-y-1 transition-all duration-500">
                  <div className="flex items-center justify-between">
                    <span className="w-11 h-11 rounded-full bg-creamdark flex items-center justify-center text-forest group-hover:bg-forest group-hover:text-cream transition-colors duration-500"><Icon size={18} strokeWidth={1.6} /></span>
                    {badge && <span className="bg-gold text-white text-[9px] font-bold uppercase tracking-[0.15em] px-2.5 py-1 rounded-full">{badge}</span>}
                  </div>
                  <h3 className="font-serif text-xl text-forest mt-4">{title}</h3>
                  <p className="text-xs text-mutedgreen mt-2 leading-relaxed flex-1">{desc}</p>
                  <div className="flex flex-wrap gap-1.5 mt-4">
                    {tags.map((t) => <span key={t} className="text-[9px] font-semibold uppercase tracking-[0.08em] bg-[#E8F0EA] text-forest px-2 py-1 rounded-full">{t}</span>)}
                  </div>
                  <span className="link-underline inline-flex items-center gap-1.5 mt-5 text-[10px] font-bold uppercase tracking-[0.18em] text-forest self-start">
                    {cta} <MoveRight size={12} className="text-gold" />
                  </span>
                </div>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </section>

      <section className="py-20 sm:py-24">
        <div className="container-nb">
          <SectionHead eyebrow="Featured learning paths" title="Learn. Grow. Get certified." />
          <div data-testid="learning-paths-carousel" className="flex gap-5 overflow-x-auto pb-4 snap-x snap-mandatory [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
            {PATHS.map((p) => (
              <div key={p.title} className="snap-start shrink-0 w-64 bg-white rounded-2xl border border-forest/10 overflow-hidden group hover:shadow-xl hover:shadow-forest/10 transition-shadow duration-500">
                <div className="h-36 overflow-hidden"><img src={p.image} alt={p.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" /></div>
                <div className="p-5">
                  <h3 className="font-serif text-lg text-forest leading-snug">{p.title}</h3>
                  <p className="text-[11px] text-mutedgreen mt-1.5">{p.sub}</p>
                  <div className="mt-4 h-1 bg-creamdark rounded-full overflow-hidden">
                    <div className="h-full bg-gold rounded-full" style={{ width: `${p.progress}%` }} />
                  </div>
                  <p className="mt-2 text-[10px] text-mutedgreen">{p.progress}% Complete</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="pb-20 sm:pb-28">
        <div className="container-nb grid lg:grid-cols-[1.6fr_1fr] gap-8">
          <Reveal>
            <div className="h-full bg-forestdeep grain relative rounded-3xl p-8 sm:p-12">
              <div className="flex items-center gap-3">
                <span className="w-11 h-11 rounded-full border border-goldlight/40 flex items-center justify-center text-goldlight"><MessageCircleQuestion size={19} /></span>
                <h3 className="font-serif text-2xl sm:text-3xl text-cream">Ask Nutribest</h3>
              </div>
              <p className="mt-3 text-cream/70 text-sm">Have a nutrition or ingredient question? Our experts are here to help you understand ingredients, nutrition and healthy living better.</p>
              <form data-testid="ask-nutribest-form" onSubmit={ask} className="mt-8 grid sm:grid-cols-2 gap-4">
                <input data-testid="ask-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Your name" className="bg-cream/10 border border-cream/20 rounded-xl px-5 py-3.5 text-sm text-cream placeholder:text-cream/40 focus:outline-none focus:border-goldlight transition-colors" />
                <input data-testid="ask-email-input" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Your email" className="bg-cream/10 border border-cream/20 rounded-xl px-5 py-3.5 text-sm text-cream placeholder:text-cream/40 focus:outline-none focus:border-goldlight transition-colors" />
                <textarea data-testid="ask-message-input" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} placeholder="Your question..." rows={3} className="sm:col-span-2 bg-cream/10 border border-cream/20 rounded-xl px-5 py-3.5 text-sm text-cream placeholder:text-cream/40 focus:outline-none focus:border-goldlight transition-colors resize-none" />
                <button data-testid="ask-submit-button" disabled={busy} className="sm:col-span-2 group inline-flex items-center justify-center gap-2 bg-gold hover:bg-goldlight text-white text-xs font-bold uppercase tracking-[0.18em] py-4 rounded-full transition-colors disabled:opacity-60">
                  Send Your Question <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
                </button>
              </form>
            </div>
          </Reveal>
          <Stagger className="flex flex-col gap-4">
            {STATS.map(({ icon: Icon, value, label }) => (
              <StaggerItem key={label} className="flex-1">
                <div className="h-full bg-white border border-forest/10 rounded-2xl p-6 flex items-center gap-5 hover:border-gold/40 transition-colors duration-500">
                  <span className="w-12 h-12 rounded-full bg-creamdark flex items-center justify-center text-forest shrink-0"><Icon size={20} strokeWidth={1.5} /></span>
                  <div>
                    <p className="font-serif text-3xl text-forest">{value}</p>
                    <p className="text-[11px] text-mutedgreen uppercase tracking-[0.12em] font-semibold">{label}</p>
                  </div>
                </div>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </section>

      <section className="pb-20 sm:pb-28">
        <div className="container-nb grid lg:grid-cols-2 gap-0 rounded-3xl overflow-hidden border border-forest/10">
          <div className="p-8 sm:p-12 flex flex-col justify-center bg-white order-2 lg:order-1">
            <h3 className="font-serif text-3xl sm:text-4xl text-forest leading-tight">The More You Know,<br />The Better You Nourish.</h3>
            <p className="mt-5 text-sm text-mutedgreen leading-relaxed max-w-md">At Nutribest, education is part of our promise. Because every family deserves not only food they can trust — but also knowledge they can trust.</p>
            <span className="mt-7 inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-forest">Explore. Learn. Thrive. <MoveRight size={14} className="text-gold" /></span>
          </div>
          <ParallaxImage src={IMG.familyDinner} alt="Family learning together over a meal" className="h-72 lg:h-auto order-1 lg:order-2" speed={8} />
        </div>
      </section>
    </div>
  );
}
